/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */
/**
 * The Launcher Client handles spawning windows of all kinds.
 * Finsemble provides the architecture to launch, resize, and reposition any component, whether native, modern, or third-party.
 *
 *
 * The Launcher API has capabilities to customize your end user's experience.
 * This includes CSS-like positioning and a fully display-aware positioning that deals with idiosyncrasies such as monitors with different scaling resolutions.
 *
 *
 * CSS provides higher level abstractions that aid in laying out an application that is composed of constituent parts.
 * Finsemble has borrowed CSS’s positioning paradigm and applied it to the task of laying out windows on the desktop.
 * This CSS-style positioning allows windows to be positioned on the `left`, `right`, `top`, or `bottom` of the end user’s screen for instance; we also developed new positions, such as `adjacent`, which allows a child window to spawn adjacent to their parent.
 * Components can be positioned and sized by percentage, relative to the monitor or to each other (nested windows).
 *
 * The Launcher Client frequently uses the parameters <code>windowName</code> and <code>componentType</code>. [Learn more about them here](/docs/smart-desktop/windows-and-workspaces/ComponentTypesAndWindowNames).
 * @module LauncherClient
 */
import { getMyWindowIdentifier as getMyWindowIdentifierUtil, clone } from "../common/util";
import Logger from "./logger";
import { FinsembleWindow } from "../common/FinsembleWindow";
import { SpawnParams } from "../services/window/types";
import { StandardError, StandardErrorCallback, StandardPromise } from "../types";
import { WindowIdentifier, WindowDescriptor, WindowLocator, ShowWindowParams } from "../services/window/types";
import { RouterClient } from "./routerClient";
import SystemManagerClient from "../common/systemManagerClient";
import {
	MonitorCommand,
	MonitorInfoDetail,
	getAllMonitorsClientSide,
	getWhichMonitorClientSide,
} from "../common/Monitors";
import {
	registerComponent,
	unRegisterComponent,
	getMonitor,
	getActiveAndPendingWindowNames,
	getPendingWindows,
	getComponentsThatCanReceiveDataTypes,
	hyperFocus,
	createWindowGroup,
	addWindowsToGroup,
	removeWindowsFromGroup,
	getGroupsForWindow,
	addToGroups,
} from "../deprecated/deprecatedlauncherClient";
import { bringWindowsToFront, minimizeWindows } from "../common/Desktop";
import { isMouseOverDOMElement } from "../common/util";

// A map of related menus that is kept by handleToggle.
const okayToOpenMenu = {};

export type ComponentDescriptor = Record<string, any>;
export type ComponentList = Record<string, ComponentDescriptor>;
export type MonitorInfo = any;
export type SpawnResponse = {
	windowDescriptor: WindowDescriptor;
	windowIdentifier: WindowIdentifier;
	finWindow: FinsembleWindow;
	launchGroupWindows?: string[];
};

/**
 * Utility function that Converts a WindowLocator into a WindowIdentifier
 * @param locator The window locator Object to convert
 * @returns WindowIdentifier
 */
const locatorToIdentifier = (locator: WindowLocator): WindowIdentifier => {
	let windowIdentifier: any = {};
	if ("windowName" in locator) {
		windowIdentifier.windowName = locator.windowName;
	} else {
		windowIdentifier.windowName = "";
	}
	if ("componentType" in locator) {
		windowIdentifier.componentType = locator.componentType;
	}
	if ("uuid" in locator) {
		windowIdentifier.uuid = locator.uuid;
	}
	return windowIdentifier;
};

/**
 * Get a list of registered components (those that were entered into <i>components.json</i>).
 *
 * @param cb Callback returns an object map of components. Each component object
 * contains the default config for that component.
 */
export const getComponentList = async (cb?: StandardErrorCallback<ComponentList>): StandardPromise<ComponentList> => {
	await SystemManagerClient.waitForStartup("windowService");
	const { err, response } = await RouterClient.query("Launcher.componentList", {});
	cb?.(err, response.data);
	return { err, data: response.data };
};

/**
 * Get the component config (from <i>components.json</i>) for a specific component.
 *
 * @param componentType The type of the component.
 * @param cb Callback returns the default config (windowDescriptor) for the requested componentType.
 */
export const getComponentDefaultConfig = async (
	componentType: string,
	cb?: StandardErrorCallback<ComponentDescriptor>
): StandardPromise<ComponentDescriptor> => {
	const { err, data } = await getComponentList();
	const componentDescriptor = data?.[componentType];
	cb?.(err, componentDescriptor);
	return { err, data: componentDescriptor };
};

/**
 * Gets monitorInfo (dimensions and position) for all monitors. Returns an array of monitorInfo objects. See <a href="/docs/smart-desktop/windows-and-workspaces/API-LauncherClient#getMonitorInfo">LauncherClient#getMonitorInfo</a> for the format of a monitorInfo object.
 *
 * @param cb Returns an array of monitorInfo objects.
 */
export const getMonitorInfoAll = async (
	cb?: StandardErrorCallback<MonitorInfoDetail[]>
): StandardPromise<MonitorInfoDetail[]> => {
	Logger.system.debug("LauncherClient.getMonitorInfoAll()");
	return getAllMonitorsClientSide(RouterClient, cb);
};

/**
 * Gets monitor information for a given windowIdentifier or for a specific monitor.
 * If neither the identifier or monitor are provided then the monitorInfo for the current window is returned.
 *
 *
 * The information returned contains:
 *
 * **monitorRect** - The full dimensions for the monitor. <br/>
 * **availableRect** - The dimensions for the available space on the monitor (less the Windows task bar). <br/>
 * **position** - The position of the monitor, numerically from zero to X. Primary monitor is zero. <br/>
 * **whichMonitor** - Contains the string "primary" if it is the primary monitor. <br/>
 *
 * The dimensions are supplemented with the following additional members:
 *
 * **width** - The width as calculated (right - left). <br/>
 * **height** - The height as calculated (bottom - top). <br/>
 *
 * @param params
 * @param params.windowIdentifier The windowIdentifier to get the monitorInfo. If undefined, then the current window.
 * @param params.monitor If passed then a specific monitor is identified. Valid values include:
 *
 * <b>"mine"</b> - Place the window on the same monitor as the calling window.
 *
 * Integer value from 0-n (0 being the primary monitor).
 *
 * <b>"primary"</b> indicates the user's primary monitor.
 *
 * <b>"all"</b> - Put a copy of the component on all monitors.
 * @param cb Returns a monitorInfo object containing the monitorRect and availableRect.
 *
 * @returns A promise that resolves to a monitorInfo
 */
export const getMonitorInfo = async (
	params: {
		monitor?: MonitorCommand;
		windowIdentifier?: WindowIdentifier;
	} = {},
	cb?: StandardErrorCallback<MonitorInfoDetail>
): StandardPromise<MonitorInfoDetail> => {
	Logger.system.debug("LauncherClient.getMonitorInfo()");

	if ((params.monitor as string) === "all") {
		Logger.deprecated(
			`Use of type "all" in LauncherClient.getMonitorInfo() is deprecated. Pleae use LauncherClient.getMonitorInfoAll()`
		);
		return getMonitorInfoAll(cb as any) as any;
	}
	return getWhichMonitorClientSide(RouterClient, params, cb);
};

/**
 * Displays a window and relocates/resizes it according to the values contained in parameters. If the specified window is in a group or tabbed, it will be unsnapped/ungrouped/untabbed from the other windows.
 * If invoked on a tabbed window or a window in a group, the window will be removed from the tab/group.
 *
 * Caution: A window can inherit parameters only once, at the time when it’s spawned. When the component already exists, changing parameters other than positioning arguments (monitor, relativeWindow, top, bottom, left, right, width, height) has no effect.
 *
 * ```javascript
 * FSBL.Clients.LauncherClient.showWindow({windowName: "Welcome Component-86-3416-Finsemble", componentType: "Welcome Component"}, {spawnIfNotFound: true}, (err, data)=> {});
 * ```
 * @param windowIdentifier A windowIdentifier. This is an object containing windowName and componentType. If windowName is not given, Finsemble will try to find it by componentType.
 * @param params Configuration settings from the <i><a href="/docs/smart-desktop/configuration/ConfigReference#finsemble-appd---manifest-window">finsemble.appd[].manifest.window</a></i> section of the application manifest may be used as parameters to this function, except for the `options` element.
 * These are the same as <a href="/docs/smart-desktop/windows-and-workspaces/API-LauncherClient#spawn">LauncherClient.spawn()</a> with the following exceptions:
 * <ul>
 * 	<li><b>monitor</b> - Same as spawn() except that null or undefined means don't move the window to a different monitor.</li>
 *  <li><b>left</b> - Same as spawn() except that null or undefined means don't move the window from current horizontal location.</li>
 *  <li><b>top</b> - Same as spawn() except that null or undefined means don't move the window from current vertical location.</li>
 *  <li><b>slave</b> - Cannot be set for an existing window. Will only go into effect if the window is spawned. (In other words, only use this in conjunction with spawnIfNotFound).</li>
 *  <li><b>spawnIfNotFound</b> - If true, then spawns a new window if the requested one cannot be found. Note: only works if the windowIdentifier contains a componentType.</li>
 *  <li><b>autoFocus</b> - Whether or not the window should be focused when shown.</li>
 * </ul>
 * @param cb Callback to be invoked after the function is completed regardless if the call fails or succeeds.
 * Callback contains two arguments in the following order.
 * <ol>
 * 	<li><b>error</b>: an object containing details related to the reason the call failed and where the exception occurred. This object will be null if the call has succeeded.</li>
 * 	<li><b>data</b>: an object with the following information:
 * 		<ul>
 * 			<li><b>windowIdentifier</b> - The <a href="/docs/smart-desktop/windows-and-workspaces/ComponentTypesAndWindowNames">WindowIdentifier</a> for the new window.</li>
 * 			<li><b>windowDescriptor</b> - The <a href="/docs/smart-desktop/windows-and-workspaces/ComponentTypesAndWindowNames">WindowDescriptor</a> of the new window.</li>
 * 			<li><b>finWindow</b> - A <code>finWindow</code> object referencing the new window.</li>
 * 		</ul>
 * 	</li>
 * </ol>
 */
export const showWindow = async (
	windowLocator: WindowLocator,
	params: ShowWindowParams,
	cb?: StandardErrorCallback<SpawnResponse>
): StandardPromise<SpawnResponse> => {
	if (!params) {
		params = {};
	}
	params = clone(params);
	if (!params.staggerPixels && params.staggerPixels !== 0) {
		params.staggerPixels = 100;
	}
	params.windowIdentifier = locatorToIdentifier(windowLocator);

	const myWindowIdentifier = await getMyWindowIdentifierUtil();
	if (!params.relativeWindow) {
		params.relativeWindow = myWindowIdentifier;
	}

	await SystemManagerClient.waitForStartup("windowService");
	const { err, response } = await RouterClient.query("Launcher.showWindow", params);
	if (err) {
		cb?.(err);
		return { err };
	} else {
		const name = response.data.windowIdentifier.windowName;
		const { wrap: finWindow } = await FinsembleWindow.getInstance({
			name,
		});
		const data: SpawnResponse = {
			...response.data,
			windowIdentifier: {
				...response.data.windowIdentifier,
				name,
			},
			finWindow,
		};

		cb?.(err, data);
		return { err, data: data };
	}
};

/**
 * A convenient method for dealing with a common use-case, which is toggling the appearance and disappearance of a child window when a button is pressed, aka drop down menus.
 * Call this method from the click handler for your element. Your child window will need to close itself on blur events.
 * Caution: A window can inherit parameters only once, at the time when it’s spawned. When the component already exists, changing parameters other than positioning arguments (monitor, relativeWindow, top, bottom, left, right, width, height) has no effect.
 * @param element The DOM element, or selector, clicked by the end user.
 * @param windowIdentifier Identifies the child window
 * @param params Parameters to be passed to <a href="/docs/smart-desktop/windows-and-workspaces/API-LauncherClient#showWindow">LauncherClient.showWindow()</a> if the child window is allowed to open
 */
export const toggleWindowOnClick = (
	element: HTMLElement,
	windowLocator: WindowLocator,
	params: ShowWindowParams
): void => {
	const windowIdentifier = locatorToIdentifier(windowLocator);

	const key = windowIdentifier.windowName ?? windowIdentifier.componentType ?? "";
	// If the element was clicked while the menu was open then return right away. The menu window will receive a blur event and close.
	// This method is dependent on the fact that blur events are processed before click events.
	// If this turns out to be a problem then put this call inside of a setTimeout().
	if (okayToOpenMenu[key] === false) {
		okayToOpenMenu[key] = true;
		return;
	}
	const onDisplayed = (showError, showResponse) => {
		if (!showResponse) return;
		const { finWindow } = showResponse;
		const onBlur = () => {
			okayToOpenMenu[key] = true;
			isMouseOverDOMElement(element, (mouseIsOverElement) => {
				okayToOpenMenu[key] = !mouseIsOverElement;
			});
			finWindow.removeEventListener("blurred", onBlur);
		};
		finWindow.addEventListener("blurred", onBlur);
	};
	if (!params.position) params.position = "absolute";
	showWindow(windowLocator, params, onDisplayed);
};

const waitForReadySignal = (name: string) =>
	new Promise<StandardError>((resolve) => {
		// Wait for a definite message that the component is ready (that FSBL has been initialized in the spawned app).
		const subscriberID = RouterClient.subscribe(`Finsemble.${name}.componentReady`, (error, response) => {
			if (error || !response.data) {
				Logger.system.error(`RouterClient.waitForReadySignal() Error subscribing for component ready state ${error}`);
				resolve(error);
				// Ignore the initial "uninitialized" state message delivered by subscribe (the one with an empty string).
			} else if (Object.keys(response.data).length) {
				// A second message will arraive with the actual data. Now we know that the client has initialized and signaled its readiness.
				RouterClient.unsubscribe(subscriberID);
				resolve(null);
			}
		});
	});

const callSpawn = async (params: SpawnParams): Promise<{ err?: StandardError; result?: SpawnResponse }> => {
	const { err, response } = await RouterClient.query("Launcher.spawn", params);
	Logger.system.debug("CallSpawn", "Initial launcher callback params", err, response);
	if (err || !response) {
		const error = err ?? `Internal error: callSpawn(): No data returned from service for "Launcher.spawn"`;
		return { err: error };
	} else {
		const { data: spawnResponseData } = response;

		let spawnedWindowNames: string[] = [];
		let spawnResponse;
		// If we receive an array of spawn response means we've spawned a lauch group.
		// We select the frist spawn response as the result so that a finWindow is available,
		// additionally we put all the window names in an array and assigned it to a launchGroupWindows
		// property to be placed in the result object so that we have all availble window names for the
		// launch group
		if (Array.isArray(spawnResponseData)) {
			spawnedWindowNames = spawnResponseData.reduce((acc, curr) => {
				acc.push(curr.windowIdentifier.windowName);
				return acc;
			}, []);
			spawnResponse = spawnResponseData[0];
		} else {
			spawnResponse = spawnResponseData;
		}

		const name = spawnResponse.windowIdentifier.windowName;
		const { wrap: finWindow } = await FinsembleWindow.getInstance({
			name,
		});
		const result = {
			...spawnResponse,
			windowIdentifier: {
				...spawnResponse.windowIdentifier,
				name,
			},
			// Add a wrapped finWindow to the response (this can only be done client side)
			finWindow,
		};

		// if we have an array of window names - means spawn of launchGroup
		// so lets add it to the result
		if (spawnedWindowNames.length > 0) {
			result.launchGroupWindows = spawnedWindowNames;
		}

		Logger.system.debug("CallSpawn", "Waiting for ready signal", name);
		const waitError = await waitForReadySignal(name);
		Logger.system.debug("CallSpawn", "Got ready signal", name);
		return { err: waitError, result };
	}
};

/**
 * Asks the Launcher service to spawn a new component. Any parameter below can also be specified in <i>public/config/components.json</i>, which will
 * then operate as the default for that value.
 *
 * The launcher parameters mimic CSS window positioning.
 * For instance, to set a full screen window use `{left: 0, top: 0, right: 0, bottom: 0}`.
 * This is functionally equivalent to: `{left: 0, top: 0, width: "100%", height: "100%"}`.
 *
 * You can also spawn more than one component at a time by using a single action, such as clicking a button on a menu.
 * See https://github.com/ChartIQ/finsemble-seed/tree/recipes/SpawnComponentGroup for an example of spawning a component group.
 *
 * ```javascript
 * FSBL.Clients.LauncherClient.spawn("Welcome Component", {left: 0, top: 0, position: "relative"}, (err, data)=> {});
 * ```
 * @param component The application or component to spawn.
 * @param params Configuration settings from the <i><a href="/docs/smart-desktop/configuration/ConfigReference#finsemble-appd---manifest-window">finsemble.appd[].manifest.window</a></i> section of the application manifest may be used as parameters to this function, except for the `options` element.
 * @param cb Function invoked after the window is created
 */
export const spawn = async (
	component: string,
	params: SpawnParams,
	cb?: StandardErrorCallback<SpawnResponse>
): Promise<{ error?: StandardError; response?: SpawnResponse }> => {
	if (!params) {
		params = {};
	}
	params = clone(params);
	params.component = component;
	if (!params.options) {
		params.options = {};
	}
	if (!params.options.customData) {
		params.options.customData = {};
	}

	if (!params.staggerPixels && params.staggerPixels !== 0) {
		params.staggerPixels = 50;
	}
	Logger.system.debug(`LauncherClient.spawn starting for componentType:${component}`);

	await SystemManagerClient.waitForStartup("windowService");
	const windowIdentifier = await getMyWindowIdentifierUtil();
	params.launchingWindow = windowIdentifier;
	const { err, result: response } = await callSpawn(params);
	Logger.system.debug(`LauncherClient.spawn completing for componentType:${component}`, err);
	cb?.(err, response);
	return { error: err, response: response };
};

/**
 * Returns a windowIdentifier for the current window.
 *
 * @param cb Callback function returns windowIdentifier for this window (optional or use the returned Promise)
 * @returns A promise that resolves to a windowIdentifier
 */
export const getMyWindowIdentifier = async (
	cb?: (windowIdentifier: WindowIdentifier) => void
): Promise<WindowIdentifier> => {
	const wi = await getMyWindowIdentifierUtil();
	cb?.(wi);
	return wi;
};

/**
 * Gets the windowDescriptor for all open windows.
 *
 * **Note:** This returns descriptors even if the window is not part of the workspace.
 *
 * ```javascript
 * FSBL.Clients.LauncherClient.getActiveDescriptors(function (error, response) {
 * 	if (error) {
 *		Logger.system.error("Error getting active descriptors: ",  + JSON.stringify(error));
 *	} else {
 *		const activeDescriptors = response;
 *		//{
 *		//	...
 *		//	Toolbar-1-Finsemble: {...},
 *		//	Welcome-Component-80-6453-Finsemble: {...},
 *		//	...
 *		//}
 * 	}
 * });
 * ```
 * @param cb Callback returns an object containing windowDescriptors keyed by name.
 */
export const getActiveDescriptors = async (
	cb?: StandardErrorCallback<Record<string, WindowDescriptor>>
): StandardPromise<Record<string, WindowDescriptor>> => {
	await SystemManagerClient.waitForStartup("windowService");
	const { err, response } = await RouterClient.query("Launcher.getActiveDescriptors", {});
	if (err) Logger.system.error(err);
	cb?.(err, response.data);
	return { err, data: response.data };
};

/**
 * @private Adds a custom component.
 * @ignore
 */
export const addUserDefinedComponent = async (
	params: {
		name: string;
		url: string;
	},
	cb?: StandardErrorCallback<void>
): StandardPromise<void> => {
	await SystemManagerClient.waitForStartup("windowService");
	const { err } = await RouterClient.query("Launcher.userDefinedComponentUpdate", {
		type: "add",
		name: params.name,
		url: params.url,
	});
	if (err) Logger.system.error(err);
	cb?.(err);
	return { err };
};

/**
 * @private Adds a custom component.
 * @ignore
 */
export const removeUserDefinedComponent = async (
	params: {
		name: string;
		url?: string;
	},
	cb?: StandardErrorCallback<void>
): StandardPromise<void> => {
	await SystemManagerClient.waitForStartup("windowService");
	const { err } = await RouterClient.query("Launcher.userDefinedComponentUpdate", {
		type: "remove",
		name: params.name,
		url: params.url,
	});
	if (err) Logger.system.error(err);
	cb?.(err);
	return { err };
};

/**
 * @ignore
 */
export const LauncherClient = {
	/**
	 * For backward compatibility
	 * @private
	 * @deprecated
	 */
	initialize: () => {
		Logger.system.deprecated("It is no longer necessary to call LauncherClient.initialized()");
	},
	/**
	 * For backward compatibility
	 * @private
	 * @deprecated
	 */
	onReady: (cb?: any) => {
		Logger.system.deprecated("It is no longer necessary to call LauncherClient.onReady()");
		cb?.();
	},
	addToGroups,
	getGroupsForWindow,
	removeWindowsFromGroup,
	addWindowsToGroup,
	createWindowGroup,
	minimizeWindows,
	hyperFocus,
	bringWindowsToFront,
	getComponentsThatCanReceiveDataTypes,
	removeUserDefinedComponent,
	addUserDefinedComponent,
	getPendingWindows,
	getActiveAndPendingWindowNames,
	getActiveDescriptors,
	getMyWindowIdentifier,
	getMonitor,
	spawn,
	toggleWindowOnClick,
	showWindow,
	unRegisterComponent,
	registerComponent,
	getMonitorInfo,
	getMonitorInfoAll,
	getComponentDefaultConfig,
	getComponentList,
};

/**
 * @ignore
 */
export default LauncherClient;
